import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    Edit2,
    Trash2,
    Activity,
    Globe,
    Plus,
    TrendingUp,
    AlertTriangle,
    X,
} from "lucide-react";
import Title from "../../components/title/Title.tsx";
import AccountModal from "../../components/accountModal/AccountModal.tsx";
import { Account } from "../../interfaces/AccountData.js";
import "./Accounts.css";
import { mapCompositeToAccount, mapAccountToComposite } from "../../components/AccountHelpers.ts";
import { useAuth } from "../../contexts/AuthContext.js";


interface DeleteConfirmationProps {
    isOpen: boolean;
    accountName: string;
    onConfirm: () => void;
    onCancel: () => void;
}

const DeleteConfirmation: React.FC<DeleteConfirmationProps> = ({
    isOpen,
    accountName,
    onConfirm,
    onCancel,
}) => {
    if (!isOpen) return null;

    return (
        <div className="delete-confirmation-overlay">
            <div className="delete-confirmation-modal">
                <div className="delete-confirmation-header">
                    <AlertTriangle size={20} />
                    <h3>Confirm Deletion</h3>
                    <button className="delete-close-button" onClick={onCancel}>
                        <X size={18} />
                    </button>
                </div>
                <div className="delete-confirmation-content">
                    <p>
                        Are you sure you want to delete{" "}
                        <span className="delete-account-name">{accountName}</span>?
                    </p>
                    <p className="delete-warning">This action cannot be undone.</p>
                </div>
                <div className="delete-confirmation-actions">
                    <button className="delete-cancel-button" onClick={onCancel}>
                        Cancel
                    </button>
                    <button className="delete-confirm-button" onClick={onConfirm}>
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};

const Accounts: React.FC = () => {
    const { user } = useAuth();
    const [accounts, setAccounts] = useState < Account[] > ([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentAccount, setCurrentAccount] = useState < Account | undefined > (undefined);
    const [deleteConfirmation, setDeleteConfirmation] = useState < {
        isOpen: boolean;
        accountId: string;
        accountName: string;
    } > ({
        isOpen: false,
        accountId: "",
        accountName: "",
    });

    // Fetch accounts from the composite endpoint.
    useEffect(() => {
        axios
            .get("/account/composite")
            .then((response) => {
                const mappedAccounts = response.data.map(mapCompositeToAccount);
                setAccounts(mappedAccounts);
                setIsLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching accounts:", error);
                setIsLoading(false);
            });
    }, []);

    const openDeleteConfirmation = (id: string, name: string) => {
        setDeleteConfirmation({ isOpen: true, accountId: id, accountName: name });
    };

    const closeDeleteConfirmation = () => {
        setDeleteConfirmation({ isOpen: false, accountId: "", accountName: "" });
    };

    const confirmDeleteAccount = () => {
        axios
            .delete(`/account/delete/${deleteConfirmation.accountId}`)
            .then(() => {
                setAccounts((prev) =>
                    prev.filter((account) => account.id !== deleteConfirmation.accountId)
                );
                closeDeleteConfirmation();
            })
            .catch((error) => {
                console.error("Error deleting account:", error);
                alert("Failed to delete account.");
            });
    };

    const handleAddAccount = () => {
        setCurrentAccount(undefined);
        setIsModalOpen(true);
    };

    const handleEditAccount = (account: Account) => {
        setCurrentAccount(account);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentAccount(undefined);
    };

    const handleSaveAccount = async (data: Account) => {
        //const compositeData = mapAccountToComposite(data);
        try {
            refreshAccounts(); // Refresh the entire account list
        } catch (error) {
            console.error("Error saving account:", error);
            alert("Failed to save account.");
        }
    };


    const refreshAccounts = () => {
        setIsLoading(true);
        axios
            .get("/account/composite")
            .then((response) => {
                const mappedAccounts = response.data.map(mapCompositeToAccount);
                setAccounts(mappedAccounts);
                setIsLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching accounts:", error);
                setIsLoading(false);
            });
    };

    // Split accounts into sections based on the “type” property.
    const privateAccounts = accounts.filter((account) => account.type === "Personal");
    const sharedAccounts = accounts.filter((account) => account.type === "Shared");

    const renderAccount = (account: Account) => (
        <div key={account.id} className="accounts-card">
            <div className="accounts-card-header">
                <h2 className="accounts-card-title">{account.name}</h2>
                <div className="accounts-actions">
                    <button
                        className="accounts-action-button accounts-edit-button"
                        onClick={() => handleEditAccount(account)}
                        aria-label="Edit account"
                    >
                        <Edit2 size={16} />
                    </button>
                    {(!account || account.ownerUserId === user.user.userId.value) && (
                        <button
                            className="accounts-action-button accounts-delete-button"
                            onClick={() => openDeleteConfirmation(account.id, account.name)}
                            aria-label="Delete account"
                        >
                            <Trash2 size={16} />
                        </button>
                    )}
                </div>
            </div>
            <div className="accounts-card-content">
                <div className="accounts-info-item">
                    <div className="accounts-info-icon accounts-status-icon">
                        <Activity size={16} />
                    </div>
                    <div className="accounts-info-content">
                        <span className="accounts-info-label">Status</span>
                        <span className="accounts-info-value accounts-status">
                            {account.isActive ? "Active" : "Inactive"}
                        </span>
                    </div>
                </div>
                <div className="accounts-info-item">
                    <div className="accounts-info-icon accounts-environment-icon">
                        <Globe size={16} />
                    </div>
                    <div className="accounts-info-content">
                        <span className="accounts-info-label">Environment</span>
                        <span className="accounts-info-value accounts-environment">
                            {account.isTestnet ? "Testnet" : "Mainnet"}
                        </span>
                    </div>
                </div>
                <Link to={`/account/trades/${account.id}`} className="accounts-trades-button">
                    <TrendingUp size={16} />
                    <span>View Trades</span>
                </Link>
            </div>
        </div>
    );

    const AccountsSection = ({
        title,
        accountsData,
    }: {
        title: string;
        accountsData: Account[];
    }) => (
        <div className="accounts-section">
            <div className="accounts-header">
                <Title text={title} size="xl" align="left" />
                <button className="accounts-add-button" onClick={handleAddAccount}>
                    <Plus size={18} />
                    <span>Add New Account</span>
                </button>
            </div>
            {isLoading ? (
                <div className="accounts-loading">
                    <div className="accounts-spinner"></div>
                </div>
            ) : (
                <div className="accounts-grid">
                    {accountsData.length === 0 ? (
                        <div className="accounts-empty-state">No accounts found</div>
                    ) : (
                        accountsData.map(renderAccount)
                    )}
                </div>
            )}
        </div>
    );

    return (
        <div className="accounts-container">
            <AccountsSection title="Private Accounts" accountsData={privateAccounts} />
            <AccountsSection title="Shared Accounts" accountsData={sharedAccounts} />
            <AccountModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveAccount}
                account={currentAccount}
            />
            <DeleteConfirmation
                isOpen={deleteConfirmation.isOpen}
                accountName={deleteConfirmation.accountName}
                onConfirm={confirmDeleteAccount}
                onCancel={closeDeleteConfirmation}
            />
        </div>
    );
};

export default Accounts;
